import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/SossietyDialog';
import { SossietyDialogHeader, SossietyDialogClose } from '@/components/ui/SossietyDialogHeader';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ArrowLeft, ChevronDown } from 'lucide-react';
import { useToast } from '@/shared/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useAgendaConfig } from '@/features/calendar/hooks/useAgendaConfig';
import { useI18n } from '@/contexts/I18nContext';

interface AgendaConfigurationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfigSaved?: () => void;
}

interface WorkingDay {
  day: string;
  label: string;
  isSelected: boolean;
  startTime: string;
  endTime: string;
}

export const AgendaConfigurationModal: React.FC<AgendaConfigurationModalProps> = ({
  isOpen,
  onClose,
  onConfigSaved
}) => {
  const { user } = useAuth();
  const { toast } = useToast();
  const { t, currentLanguage } = useI18n();
  const { config: existingConfig, updateConfig, isUpdating, isLoading: configLoading, refetch: refetchConfig } = useAgendaConfig();
  
  // Refetch config when modal opens to ensure we have latest data
  useEffect(() => {
    if (isOpen && user) {
      // Force refetch to get latest data including class_value
      refetchConfig();
    }
  }, [isOpen, user, refetchConfig]);
  
  // Time options in 30-minute intervals (same as AddTrainingModal)
  const TIME_OPTIONS = [
    '12:00 AM', '12:30 AM',
    '1:00 AM', '1:30 AM',
    '2:00 AM', '2:30 AM',
    '3:00 AM', '3:30 AM',
    '4:00 AM', '4:30 AM',
    '5:00 AM', '5:30 AM',
    '6:00 AM', '6:30 AM',
    '7:00 AM', '7:30 AM',
    '8:00 AM', '8:30 AM',
    '9:00 AM', '9:30 AM',
    '10:00 AM', '10:30 AM',
    '11:00 AM', '11:30 AM',
    '12:00 PM', '12:30 PM',
    '1:00 PM', '1:30 PM',
    '2:00 PM', '2:30 PM',
    '3:00 PM', '3:30 PM',
    '4:00 PM', '4:30 PM',
    '5:00 PM', '5:30 PM',
    '6:00 PM', '6:30 PM',
    '7:00 PM', '7:30 PM',
    '8:00 PM', '8:30 PM',
    '9:00 PM', '9:30 PM',
    '10:00 PM', '10:30 PM',
    '11:00 PM', '11:30 PM'
  ];

  // Helper function to format time input to AM/PM format (same as AddTrainingModal)
  const formatTimeToAMPM = (timeInput: string): string | null => {
    if (!timeInput) return null;
    
    // If already in AM/PM format, return as is
    if (timeInput.includes('AM') || timeInput.includes('PM')) {
      return timeInput;
    }
    
    // Handle 24-hour format (HH:MM)
    const hour24Regex = /^(\d{1,2}):(\d{2})$/;
    const hour24Match = timeInput.match(hour24Regex);
    
    if (hour24Match) {
      const hours = parseInt(hour24Match[1]);
      const minutes = hour24Match[2];
      
      if (hours >= 0 && hours <= 23) {
        const ampm = hours >= 12 ? 'PM' : 'AM';
        const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
        const result = `${displayHour}:${minutes} ${ampm}`;
        return result;
      }
    }
    
    // Try to parse various formats
    const timeRegex = /^(\d{1,2}):?(\d{0,2})\s*(am|pm)?$/i;
    const match = timeInput.match(timeRegex);
    
    if (match) {
      let hours = parseInt(match[1]);
      let minutes = match[2] || '00';
      const ampm = match[3];
      
      // Validate hours and minutes
      if (hours < 1 || hours > 12) return null;
      if (minutes && (parseInt(minutes) < 0 || parseInt(minutes) > 59)) return null;
      
      // Ensure minutes is 2 digits
      if (minutes.length === 1) minutes = '0' + minutes;
      if (minutes.length === 0) minutes = '00';
      
      // If no AM/PM provided, try to guess based on common training times
      let finalAmPm = ampm;
      if (!finalAmPm) {
        // Most trainings are in the evening, but morning classes exist too
        // Default to PM for times 6-11, AM for 12 and 1-5
        if (hours >= 6 && hours <= 11) {
          finalAmPm = 'PM';
        } else {
          finalAmPm = 'AM';
        }
      }
      
      const result = `${hours}:${minutes} ${finalAmPm.toUpperCase()}`;
      return result;
    }
    
    return null;
  };
  
  // Working days configuration
  const [workingDays, setWorkingDays] = useState<WorkingDay[]>([
    { day: 'mon', label: 'M', isSelected: true, startTime: '08:00', endTime: '18:00' },
    { day: 'tue', label: 'T', isSelected: true, startTime: '08:00', endTime: '18:00' },
    { day: 'wed', label: 'W', isSelected: true, startTime: '08:00', endTime: '18:00' },
    { day: 'thu', label: 'T', isSelected: true, startTime: '08:00', endTime: '18:00' },
    { day: 'fri', label: 'F', isSelected: true, startTime: '08:00', endTime: '18:00' },
    { day: 'sat', label: 'S', isSelected: false, startTime: '08:00', endTime: '18:00' },
    { day: 'sun', label: 'S', isSelected: false, startTime: '08:00', endTime: '18:00' },
  ]);
  
  // Other settings
  const [defaultDuration, setDefaultDuration] = useState(60);
  const [isPublic, setIsPublic] = useState(false);
  const [useSameHours, setUseSameHours] = useState(true);
  const [classValue, setClassValue] = useState(0.00);
  const [currency, setCurrency] = useState('USD');

  // Helper function to convert 24h format to AM/PM format
  const convert24hToAMPM = (time24h: string): string => {
    if (!time24h) return '08:00 AM';
    
    // If already in AM/PM format, return as is
    if (time24h.includes('AM') || time24h.includes('PM')) {
      return time24h;
    }
    
    // Parse 24h format (HH:MM)
    const [hours, minutes] = time24h.split(':').map(Number);
    
    if (isNaN(hours) || isNaN(minutes)) return '08:00 AM';
    
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    
    return `${displayHour}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  // Load existing configuration when modal opens or config changes
  useEffect(() => {
    if (isOpen) {
      // If config is loaded, use it; otherwise use defaults
      if (existingConfig && !configLoading) {
        // Initialize working days structure
        const daysStructure: WorkingDay[] = [
          { day: 'mon', label: 'M', isSelected: false, startTime: '08:00 AM', endTime: '06:00 PM' },
          { day: 'tue', label: 'T', isSelected: false, startTime: '08:00 AM', endTime: '06:00 PM' },
          { day: 'wed', label: 'W', isSelected: false, startTime: '08:00 AM', endTime: '06:00 PM' },
          { day: 'thu', label: 'T', isSelected: false, startTime: '08:00 AM', endTime: '06:00 PM' },
          { day: 'fri', label: 'F', isSelected: false, startTime: '08:00 AM', endTime: '06:00 PM' },
          { day: 'sat', label: 'S', isSelected: false, startTime: '08:00 AM', endTime: '06:00 PM' },
          { day: 'sun', label: 'S', isSelected: false, startTime: '08:00 AM', endTime: '06:00 PM' },
        ];
        
        // Map the data from instructor_agenda_config format
        const updatedWorkingDays = daysStructure.map(day => {
          // Check if this day is in the working_days array
          const isSelected = existingConfig.working_days?.includes(day.day) || false;
          
          // Find configuration for this day
          const dayConfig = existingConfig.working_hours?.[day.day];
          
          // Convert from 24h format (from DB) to AM/PM format (for display)
          const startTime = dayConfig?.start ? convert24hToAMPM(dayConfig.start) : '08:00 AM';
          const endTime = dayConfig?.end ? convert24hToAMPM(dayConfig.end) : '06:00 PM';
          
          return {
            ...day,
            isSelected,
            startTime,
            endTime,
          };
        });
        
        setWorkingDays(updatedWorkingDays);
        setDefaultDuration(existingConfig.default_duration || 60);
        setIsPublic(existingConfig.is_public || false);
        
        // Handle class_value - it might come as numeric from DB, convert to number
        let classValueNum = 0.00;
        if (existingConfig.class_value != null && existingConfig.class_value !== undefined) {
          if (typeof existingConfig.class_value === 'number') {
            classValueNum = existingConfig.class_value;
          } else if (typeof existingConfig.class_value === 'string') {
            classValueNum = parseFloat(existingConfig.class_value);
          } else {
            // Try to convert to number
            classValueNum = Number(existingConfig.class_value);
          }
          
          // Check if conversion was successful
          if (isNaN(classValueNum)) {
            console.warn('⚠️ Failed to parse class_value:', existingConfig.class_value);
            classValueNum = 0.00;
          }
        }
        
        setClassValue(classValueNum);
        setCurrency(existingConfig.currency || 'USD');
      } else {
        // Reset to defaults if no config exists
        setWorkingDays([
          { day: 'mon', label: 'M', isSelected: true, startTime: '08:00 AM', endTime: '06:00 PM' },
          { day: 'tue', label: 'T', isSelected: true, startTime: '08:00 AM', endTime: '06:00 PM' },
          { day: 'wed', label: 'W', isSelected: true, startTime: '08:00 AM', endTime: '06:00 PM' },
          { day: 'thu', label: 'T', isSelected: true, startTime: '08:00 AM', endTime: '06:00 PM' },
          { day: 'fri', label: 'F', isSelected: true, startTime: '08:00 AM', endTime: '06:00 PM' },
          { day: 'sat', label: 'S', isSelected: false, startTime: '08:00 AM', endTime: '06:00 PM' },
          { day: 'sun', label: 'S', isSelected: false, startTime: '08:00 AM', endTime: '06:00 PM' },
        ]);
        setDefaultDuration(60);
        setIsPublic(false);
        setClassValue(0.00);
        setCurrency('USD');
      }
    }
  }, [isOpen, existingConfig, configLoading]);

  const handleDayToggle = (dayIndex: number) => {
    const updatedDays = [...workingDays];
    updatedDays[dayIndex].isSelected = !updatedDays[dayIndex].isSelected;
    setWorkingDays(updatedDays);
  };

  const handleTimeChange = (dayIndex: number, field: 'startTime' | 'endTime', value: string) => {
    const updatedDays = [...workingDays];
    updatedDays[dayIndex][field] = value;
    setWorkingDays(updatedDays);
  };

  const handleUseSameHoursChange = (checked: boolean) => {
    setUseSameHours(checked);
    if (checked) {
      // Apply first selected day's time to all selected days
      const firstSelectedDay = workingDays.find(day => day.isSelected);
      if (firstSelectedDay) {
        const updatedDays = workingDays.map(day => ({
          ...day,
          startTime: day.isSelected ? firstSelectedDay.startTime : day.startTime,
          endTime: day.isSelected ? firstSelectedDay.endTime : day.endTime,
        }));
        setWorkingDays(updatedDays);
      }
    }
  };

  const handleSave = async () => {
    if (!user) return;

    try {
      // Create working days list and working hours mapping
      const workingDaysList = workingDays.filter(day => day.isSelected).map(day => day.day);
      
      // Ensure all times are in 24h format before saving
      const convertTo24h = (timeStr: string) => {
        if (!timeStr) return '08:00';
        
        // If already in 24h format, return as is
        if (timeStr.includes(':')) {
          const [hours, minutes] = timeStr.split(':');
          if (hours.length === 2 && !timeStr.includes('AM') && !timeStr.includes('PM')) {
            return timeStr;
          }
        }
        
        // Convert AM/PM to 24h
        const time = timeStr.toLowerCase();
        let [timePart, period] = time.split(' ');
        let [hours, minutes] = timePart.split(':').map(Number);
        
        if (period === 'pm' && hours !== 12) {
          hours += 12;
        } else if (period === 'am' && hours === 12) {
          hours = 0;
        }
        
        return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
      };
      
      const workingHours = workingDays.reduce((acc, day) => {
        if (day.isSelected) {
          acc[day.day] = { 
            start: convertTo24h(day.startTime), 
            end: convertTo24h(day.endTime) 
          };
        }
        return acc;
      }, {} as Record<string, { start: string; end: string }>);

      const configData = {
        instructor_id: user.id,
        working_days: workingDaysList,
        working_hours: workingHours,
        default_duration: defaultDuration,
        is_public: isPublic,
        class_value: classValue,
        currency: currency,
        updated_at: new Date().toISOString(),
      };

      await updateConfig(configData);

      toast({
        title: "Agenda configuration saved!",
        description: "Your working hours and preferences have been updated.",
        duration: 2000,
      });

      onClose();
      onConfigSaved?.();
    } catch (error) {
      console.error('Error saving agenda configuration:', error);
      toast({
        title: "Error",
        description: "Failed to save agenda configuration. Please try again.",
        variant: "destructive",
      });
    } finally {
      // setIsLoading(false); // This state was removed, so this line is removed.
    }
  };

  const selectedDaysCount = workingDays.filter(day => day.isSelected).length;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="w-[90vw] max-w-md bg-white rounded-2xl max-h-[90vh] flex flex-col border border-gray-200">
        <SossietyDialogHeader title={t('agenda_config.title')} titleClassName="text-lg text-black">
          <SossietyDialogClose 
            onClick={onClose} 
            className="text-gray-600 hover:text-gray-800"
          />
        </SossietyDialogHeader>

        <div className="space-y-4 pb-3 overflow-y-auto flex-1">
          {/* Agenda Privacy Section - First */}
          <div>
            <h3 className="text-base font-semibold text-black mb-2">{t('agenda_config.agenda_privacy')}</h3>
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-sm text-gray-700">{t('agenda_config.make_agenda_public')}</p>
                <p className="text-xs text-gray-500">{t('agenda_config.allow_users_see_slots')}</p>
              </div>
              <Switch
                checked={isPublic}
                onCheckedChange={setIsPublic}
                className="data-[state=checked]:bg-red-600 flex-shrink-0 ml-2"
              />
            </div>
          </div>

          {/* Class Value Section - Second */}
          <div>
            <h3 className="text-base font-semibold text-black mb-2">{t('agenda_config.class_value')}</h3>
            <p className="text-sm text-gray-600 mb-3">{t('agenda_config.class_value_description')}</p>
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={classValue === 0 || classValue === null || classValue === undefined ? '' : classValue}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === '') {
                    setClassValue(0);
                  } else {
                    const numValue = parseFloat(value);
                    setClassValue(isNaN(numValue) ? 0 : numValue);
                  }
                }}
                className="w-24 h-12 px-3 py-2 text-sm bg-white border border-gray-300 rounded-md text-black placeholder:text-gray-500 focus:border-red-500 focus:ring-red-500 focus:outline-none"
                min="0"
                step="0.01"
                inputMode="decimal"
                pattern="[0-9]*[.,]?[0-9]*"
                placeholder="0.00"
              />
              <Select
                value={currency}
                onValueChange={setCurrency}
              >
                <SelectTrigger 
                  className="w-20 h-12 px-3 py-2 bg-white text-black border border-gray-300 rounded-md focus:border-red-500 focus:ring-red-500 focus:outline-none [&_[data-radix-select-icon]]:hidden !border-gray-300"
                  style={{ border: '1px solid #d1d5db' }}
                >
                  <SelectValue placeholder="USD" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none">
                    <ChevronDown className="w-4 h-4 text-gray-600" />
                  </span>
                </SelectTrigger>
                <SelectContent className="w-[var(--radix-select-trigger-width)] max-w-[var(--radix-select-trigger-width)] bg-white border border-gray-300 py-2 space-y-1">
                  <SelectItem value="USD" className="text-black truncate">USD</SelectItem>
                  <SelectItem value="BRL" className="text-black truncate">BRL</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Working Days Section - Third */}
          <div>
            <h3 className="text-base font-semibold text-black mb-2">{t('agenda_config.working_days')}</h3>
            <p className="text-sm text-gray-600 mb-3">{t('agenda_config.working_days_description')}</p>
            
            {/* Day Selectors - Smaller size */}
            <div className="flex gap-2 mb-3 justify-center">
              {workingDays.map((day, index) => (
                <button
                  key={day.day}
                  onClick={() => handleDayToggle(index)}
                  className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium transition-all ${
                    day.isSelected
                      ? 'bg-red-600 text-white'
                      : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
                  }`}
                >
                  {day.label}
                </button>
              ))}
            </div>

            {/* Use Same Hours Checkbox */}
            {workingDays.filter(day => day.isSelected).length > 1 && (
              <div className="flex items-center space-x-2 mb-3">
                <Checkbox
                  id="use-same-hours"
                  checked={useSameHours}
                  onCheckedChange={handleUseSameHoursChange}
                  className="border-gray-400 data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
                />
                <Label htmlFor="use-same-hours" className="text-sm text-gray-700">
                  {t('agenda_config.use_same_hours')}
                </Label>
              </div>
            )}
          </div>

          {/* Class Hours Section - Fourth */}
          <div>
            <h3 className="text-base font-semibold text-black mb-2">{t('agenda_config.class_hours')}</h3>
            {workingDays
              .filter(day => day.isSelected)
              .map((day, index) => {
                const originalIndex = workingDays.findIndex(d => d.day === day.day);
                return (
                  <div key={day.day} className="flex items-center gap-2 mb-2 min-w-0">
                    <span className="text-sm text-gray-600 w-6 flex-shrink-0">{day.label}</span>
                    <div className="relative">
                      <Input
                        type="text"
                        value={day.startTime}
                        onChange={(e) => {
                          let value = e.target.value;
                          // Allow user to type freely, but we'll validate the format
                          handleTimeChange(originalIndex, 'startTime', value);
                        }}
                        onBlur={(e) => {
                          // On blur, try to format the time to AM/PM if it's a valid time
                          const value = e.target.value;
                          const formatted = formatTimeToAMPM(value);
                          if (formatted) {
                            handleTimeChange(originalIndex, 'startTime', formatted);
                          }
                        }}
                        className={`text-sm bg-white border-gray-300 text-black focus:border-red-500 focus:ring-red-500 ${useSameHours && index > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                        style={{ minWidth: '0' }}
                        list="time-options"
                        autoComplete="off"
                        disabled={useSameHours && index > 0}
                      />
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-[8px]">
                        AM/PM
                      </div>
                    </div>
                    <datalist id="time-options">
                      {TIME_OPTIONS.map((time) => (
                        <option key={time} value={time} />
                      ))}
                    </datalist>
                    <span className="text-xs text-gray-600 flex-shrink-0">to</span>
                    <div className="relative">
                      <Input
                        type="text"
                        value={day.endTime}
                        onChange={(e) => {
                          let value = e.target.value;
                          // Allow user to type freely, but we'll validate the format
                          handleTimeChange(originalIndex, 'endTime', value);
                        }}
                        onBlur={(e) => {
                          // On blur, try to format the time to AM/PM if it's a valid time
                          const value = e.target.value;
                          const formatted = formatTimeToAMPM(value);
                          if (formatted) {
                            handleTimeChange(originalIndex, 'endTime', formatted);
                          }
                        }}
                        className={`text-sm bg-white border-gray-300 text-black focus:border-red-500 focus:ring-red-500 ${useSameHours && index > 0 ? 'opacity-50 cursor-not-allowed' : ''}`}
                        style={{ minWidth: '0' }}
                        list="time-options"
                        autoComplete="off"
                        disabled={useSameHours && index > 0}
                      />
                      <div className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 text-[8px]">
                        AM/PM
                      </div>
                    </div>
                  </div>
                );
              })}
          </div>

          {/* Class Duration Section - Fifth */}
          <div>
            <h3 className="text-base font-semibold text-black mb-2">{t('agenda_config.class_duration')}</h3>
            <p className="text-sm text-gray-600 mb-2">{t('agenda_config.class_duration_description')}</p>
            <div className="flex items-center gap-2">
              <Input
                type="number"
                value={defaultDuration}
                onChange={(e) => setDefaultDuration(Number(e.target.value) || 0)}
                className="w-16 text-sm bg-white border-gray-300 text-black placeholder:text-gray-500 focus:border-red-500 focus:ring-red-500"
                min="15"
                max="180"
                step="15"
                inputMode="numeric"
                pattern="[0-9]*"
              />
              <span className="text-sm text-gray-600">{t('agenda_config.minutes')}</span>
            </div>
          </div>


        </div>

        {/* Action Buttons - Always visible at bottom */}
        <div className="flex gap-3 pt-3 pb-2 border-t border-gray-200 mt-auto bg-white">
          <Button
            variant="outline"
            onClick={onClose}
            className="flex-1 border-gray-300 text-gray-700 hover:bg-gray-50 rounded-full"
          >
            {t('agenda_config.cancel')}
          </Button>
          <Button
            onClick={handleSave}
            disabled={workingDays.filter(day => day.isSelected).length === 0 || isUpdating}
            className="flex-1 bg-red-600 text-white hover:bg-red-700 disabled:opacity-50 rounded-full"
          >
            {isUpdating ? t('agenda_config.saving') : t('agenda_config.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
